import React, { useState } from 'react';
import { useTranslation } from '@/i18n';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { Card } from '@/components/ui/cards/Card';
import { IconScout } from '@/components/ui/images/IconScout';
import { Spinner } from '@/components/ui/loading';
import { colors } from '@/assets/styles/colors';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';
import { useBulkDisableModal } from '@/hooks/checkInLocation';


export interface BulkDisableLocationModalProps {
  items: CheckInLocationItem[];
  onConfirm: () => Promise<void>;
  onClose?: () => void;
  isSelectAllMode?: boolean;
  total?: number;
  filters?: {
    hasGeolocation?: boolean | null;
    isRemoteWork?: boolean | null;
    searchText?: string | null;
    excludeLocationIds?: number[];
  };
}

export const BulkDisableLocationModal: React.FC<BulkDisableLocationModalProps> = ({
  items,
  onConfirm,
  onClose,
  isSelectAllMode = false,
  total,
  filters,
}) => {
  const { t: translate } = useTranslation('checkInLocation');
  const [isLoading, setIsLoading] = useState(false);

  const { allItems, isLoadingMore, scrollContainerRef } = useBulkDisableModal({
    isSelectAllMode,
    filters,
  });

  const excludedCount = filters?.excludeLocationIds?.length ?? 0;
  const displayItems = isSelectAllMode ? allItems : items;
  const displayCount = isSelectAllMode ? (total ?? allItems.length) - excludedCount : items.length;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      onClose?.();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Card
        elevation={2}
        padding="24px"
        borderRadius="16px"
        data-testid="bulk-disable-location-modal"
        className="flex flex-col gap-y-4 w-full max-w-[876px] max-h-[90vh] overflow-y-auto"
      >
        <div className="flex flex-col items-center gap-[10px]">
          <IconScout name="bell" size={24} color={colors.feedback.alert300} />
          <Text variant="header-6" color={colors.primary.main} className="text-center">
            {translate('bulk.modal.title')}
          </Text>
        </div>

        <Text variant="subheader-regular" color={colors.grays.neutral00}>
          {translate('bulk.modal.body')}
        </Text>

        <div
          className="border rounded-2xl overflow-hidden"
          style={{ borderColor: colors.primary.auxiliar }}
        >
          <div className="px-4 py-3">
            <Text variant="body-title-medium" color={colors.primary.dash}>
              {isSelectAllMode
                ? translate('bulk.modal.selectedCountAll', { count: displayCount })
                : translate('bulk.modal.selectedCount', { count: displayCount })}
            </Text>
          </div>
          <div
            ref={scrollContainerRef}
            className="overflow-y-auto"
            style={{ maxHeight: '427px' }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.primary.auxiliar}` }}>
                  <th
                    className="text-left px-4 py-2 font-medium"
                    style={{ color: colors.grays.neutral00 }}
                  >
                    {translate('bulk.modal.columnId')}
                  </th>
                  <th
                    className="text-left px-4 py-2 font-medium"
                    style={{ color: colors.grays.neutral00 }}
                  >
                    {translate('bulk.modal.columnName')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((item, index) => (
                  <tr
                    key={item.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? colors.grays.neutralFA : colors.grays.neutralFF,
                    }}
                  >
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {item.id}
                    </td>
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {item.name}
                    </td>
                  </tr>
                ))}
                {isLoadingMore && (
                  <tr>
                    <td colSpan={2} className="px-4 py-3 text-center">
                      <div className="flex justify-center">
                        <Spinner size="sm" />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-center gap-6">
          <PrimaryButton
            onClick={() => onClose?.()}
            disabled={isLoading}
            variant="outline"
            data-testid="bulk-disable-cancel-button"
          >
            {translate('common:actions.cancel')}
          </PrimaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            isLoading={isLoading}
            colorVariant="error"
            data-testid="bulk-disable-confirm-button"
          >
            {translate('bulk.modal.confirm')}
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

export default BulkDisableLocationModal;
